import {
  useCreateLicense,
  useDeleteLicense,
  useGetLicenseSummary,
  useListLicenses,
  useListWorkOrderAssignees,
  useUpdateLicense,
  type LicenseIn,
  type LicenseOut,
} from '@myrentalmate/api-client';
import { Chip } from '@mui/material';

import {
  optional,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { StatRow, StatTile } from '../../components/Stats';
import { listResult, okData } from '../../lib/apiData';
import { formatDate } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { RecordExtras } from '../records/RecordExtras';
import { STANDING } from './labels';

const useRows = ({ q, page, pageSize }: ListParams) =>
  listResult(useListLicenses({ q, page, page_size: pageSize }));

const COLUMNS: Column<LicenseOut>[] = [
  { label: 'License', render: (l) => l.license_type },
  { label: 'Holder', render: (l) => l.holder },
  { label: 'Number', render: (l) => l.license_number ?? '–' },
  { label: 'State', render: (l) => l.state ?? '–' },
  { label: 'Expires', render: (l) => formatDate(l.expires_on) },
  {
    label: 'Status',
    render: (l) => (
      <Chip size="small" color={STANDING[l.standing].color} label={STANDING[l.standing].label} />
    ),
  },
];

function toBody(values: FormValues): LicenseIn {
  const holder = optional(values, 'user_id');
  return {
    user_id: holder ? Number(holder) : null,
    license_type: required(values, 'license_type'),
    license_number: optional(values, 'license_number'),
    state: optional(values, 'state'),
    issued_on: optional(values, 'issued_on'),
    expires_on: required(values, 'expires_on'),
    notes: optional(values, 'notes'),
  };
}

export function LicensesPage() {
  const canManage = useCan('compliance.manage');
  const create = useCreateLicense();
  const update = useUpdateLicense();
  const remove = useDeleteLicense();
  const summary = okData(useGetLicenseSummary().data);
  const staff = okData(useListWorkOrderAssignees().data)?.items ?? [];
  const fields: Field[] = [
    {
      name: 'license_type',
      label: 'License or Certification',
      required: true,
      helperText: 'For example, Real Estate Broker',
    },
    {
      name: 'user_id',
      label: 'Holder',
      type: 'select',
      options: [
        { value: '', label: 'The Business' },
        ...staff.map((s) => ({ value: String(s.user_id), label: s.name })),
      ],
    },
    { name: 'license_number', label: 'License Number' },
    { name: 'state', label: 'State', type: 'state' },
    { name: 'issued_on', label: 'Issued', type: 'date' },
    { name: 'expires_on', label: 'Expires', type: 'date', required: true },
    { name: 'notes', label: 'Notes', type: 'multiline' },
  ];
  return (
    <RecordPage
      title="Licenses"
      singular="license"
      description="Reminders go to the holder and the business at 90, 60, 30 and 7 days, and on expiry."
      summary={
        <StatRow>
          <StatTile label="Licenses" value={summary?.total} />
          <StatTile label="Expired" value={summary?.expired} />
          <StatTile label="Expiring in 30 Days" value={summary?.expiring_30_days} />
          <StatTile label="Expiring in 90 Days" value={summary?.expiring_90_days} />
        </StatRow>
      }
      fields={fields}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={(l) => ({
        license_type: l.license_type,
        user_id: l.user_id === null ? '' : String(l.user_id),
        license_number: l.license_number ?? '',
        state: l.state ?? '',
        issued_on: l.issued_on ?? '',
        expires_on: l.expires_on,
        notes: l.notes ?? '',
      })}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) =>
        update.mutateAsync({ licenseId: recordId, data: toBody(values) })
      }
      remove={(recordId) => remove.mutateAsync({ licenseId: recordId })}
      renderExtras={(row) => (
        <RecordExtras entityType="staff_license" entityId={row.id} canManage={canManage} />
      )}
    />
  );
}
